import { Box, CircularProgress } from '@mui/joy';
import { Portal } from '@mui/material';
import { CheckCircle, Info, Warning, WarningCircle, X } from '@phosphor-icons/react';
import { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { renderHtmlMessage } from 'core/utils';
import { Color, Size, Variant } from 'theme/enums';

import { Alert, ContentWrapper, loadingIconStyle, Message, Title, Wrapper } from './style';

interface IProps {
  title: string;
  message: string | JSX.Element;
  type: ApiStatus;
  position?: NotificationPosition;
  buttons?: JSX.Element[];
  showNotification?: boolean;
  onClose?: () => void;
  withoutCloseButton?: boolean;
  style?: CSSProperties;
}

const notificationIcon = {
  [ApiStatus.SUCCESS]: <CheckCircle />,
  [ApiStatus.ERROR]: <WarningCircle />,
  [ApiStatus.LOADING]: <CircularProgress size={Size.SM} sx={loadingIconStyle} />,
  [ApiStatus.INFO]: <Info weight="fill" />,
  [ApiStatus.WARNING]: <Warning />,
};

const notificationVariant = {
  [NotificationPosition.TOP]: Variant.SOLID,
  [NotificationPosition.FORM]: Variant.SOFT,
  [NotificationPosition.DRAWER]: Variant.SOFT,
};

const notificationColor = {
  [ApiStatus.SUCCESS]: Color.SUCCESS,
  [ApiStatus.ERROR]: Color.DANGER,
  [ApiStatus.LOADING]: Color.PRIMARY,
  [ApiStatus.INFO]: Color.PRIMARY,
  [ApiStatus.WARNING]: Color.WARNING,
};

const NotificationView = ({
  title,
  message,
  type,
  position,
  buttons = [],
  showNotification,
  onClose,
  style,
  withoutCloseButton = false,
}: IProps) => {
  const { clearNotification } = useNotificationContext();
  const { t } = useTranslation();

  const variant = notificationVariant[position];

  const NotificationWrapper = useMemo(
    () => (position === NotificationPosition.TOP ? Portal : Box),
    [position]
  );

  const handleOnClose = () => {
    onClose?.();
    clearNotification();
  };

  return (
    <NotificationWrapper>
      <Wrapper notificationPosition={position} showNotification={showNotification} style={style}>
        <Alert
          startDecorator={notificationIcon[type]}
          variant={variant}
          color={notificationColor[type]}
          size={Size.LG}
          endDecorator={
            withoutCloseButton ? null : (
              <Button
                variant={variant}
                color={notificationColor[type]}
                size={Size.SM}
                text={t('buttonLabelClose')}
                endDecorator={<X />}
                onClick={handleOnClose}
              />
            )
          }
          fullWidth={NotificationPosition.TOP !== position}
          sx={{ pointerEvents: 'all' }}
        >
          <ContentWrapper>
            {title && <Title>{title}</Title>}
            {message && <Message>{renderHtmlMessage(message)}</Message>}

            {buttons.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, paddingTop: 1.5 }}>
                {buttons}
              </Box>
            )}
          </ContentWrapper>
        </Alert>
      </Wrapper>
    </NotificationWrapper>
  );
};

export default NotificationView;
